import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Gift,
  Copy,
  Check,
  Users,
  DollarSign,
  Share2,
  Star,
  Mail,
  Twitter,
  Linkedin,
  MessageSquare,
  ArrowRight,
  ArrowUpRight,
  Shield,
  Globe,
  Repeat,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const referralLink = 'https://portal.zurihub.io/signup?ref=your-code';

const steps = [
  { icon: 'Share2', title: 'Share your link', text: 'Send your unique referral link to friends, partners, and customers.' },
  { icon: 'Users', title: 'They sign up', text: 'They create a workspace and start a free trial on ZuriHub.' },
  { icon: 'Gift', title: 'Both get $100', text: 'You and your referral each earn $100 in account credit.' },
  { icon: 'DollarSign', title: 'You get $50', text: 'Earn a $50 commission after 90 days of their paid subscription.' },
];

const whyRefer = [
  { icon: 'DollarSign', title: 'Double reward', text: 'You get $50 and your friend gets $100 credit when they subscribe.' },
  { icon: 'Globe', title: 'No limits', text: 'Refer agencies, founders, support teams, or anyone who needs better CX.' },
  { icon: 'Repeat', title: 'Recurring value', text: 'Commissions are paid after 90 days of an active paid subscription.' },
  { icon: 'Shield', title: 'Trusted product', text: 'ZuriHub is used by teams in e-commerce, SaaS, health, finance, and more.' },
];

const whoCanRefer = [
  { title: 'Existing customers', text: 'Anyone with an active ZuriHub workspace gets a referral code.' },
  { title: 'Agencies and consultants', text: 'Recommend ZuriHub to clients and earn commission on each signup.' },
  { title: 'Influencers', text: 'Share ZuriHub with your audience and get paid for every subscription.' },
  { title: 'Partners', text: 'Technology and implementation partners can refer and co-sell.' },
];

const rules = [
  'Referrals must sign up using your unique referral link.',
  'The $100 credit applies after the referred workspace subscribes to a paid plan.',
  'The $50 commission is paid after the referred account stays paid for 90 days.',
  'Referrals must be new ZuriHub customers, not existing workspace owners.',
  'Self-referrals, fake accounts, and coupon stacking are not allowed.',
];

const faqs = [
  { q: 'When do I receive my $50 commission?', a: 'You receive the $50 commission after your referral has maintained a paid ZuriHub subscription for 90 days.' },
  { q: 'Is there a limit to how many people I can refer?', a: 'No. You can refer as many people as you like and earn unlimited commissions.' },
  { q: 'What if my referral signs up without my link?', a: 'The referral must sign up through your unique link for credit to be tracked.' },
  { q: 'Can I refer my own company or team?', a: 'No. Self-referrals are not eligible for credits or commissions.' },
];

const shareLinks = [
  { icon: 'MessageSquare', label: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(`Join ZuriHub - the customer experience platform built for modern teams! ${referralLink}`)}` },
  { icon: 'Twitter', label: 'Twitter', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent('Join ZuriHub - the customer experience platform built for modern teams!')}&url=${encodeURIComponent(referralLink)}` },
  { icon: 'Linkedin', label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}` },
  { icon: 'Mail', label: 'Email', url: `mailto:?subject=${encodeURIComponent('Join ZuriHub')}&body=${encodeURIComponent(`Join ZuriHub - the customer experience platform built for modern teams!\n\n${referralLink}`)}` },
];

export default function Referral() {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Offer',
      name: 'ZuriHub Refer & Earn',
      description: 'Refer a friend to ZuriHub. Both get $100 credit. You earn $50 after 90 days.',
      url: `${site.baseUrl}/referral`,
      price: '100',
      priceCurrency: 'USD',
      areaServed: 'Global',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to refer ZuriHub',
      step: steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.text,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Refer & Earn"
        description="Refer ZuriHub. You and your friend get $100 credit. You earn $50 commission after 90 days."
        path="/referral"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-semibold text-slate-300 backdrop-blur">
            <Star className="h-4 w-4 text-yellow-400" /> Refer & earn
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Earn <span className="gradient-text">$100 credit + $50</span> per referral
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Share ZuriHub with friends, partners, and customers. You both get $100 credit when they subscribe, and you earn $50 after 90 days.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Join & get your code <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/book-a-demo"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Learn more <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="steps-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="steps-heading" className="section-title">How it works</h2>
            <p className="section-subtitle mx-auto">
              Four simple steps to start earning with the ZuriHub referral program.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon === 'Share2' ? Share2 : step.icon === 'Users' ? Users : step.icon === 'Gift' ? Gift : DollarSign;
              return (
                <div
                  key={step.title}
                  className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700">
                    <StepIcon className="h-7 w-7" />
                  </div>
                  <span className="text-xs font-bold text-brand-600">Step {index + 1}</span>
                  <h3 className="mt-2 text-lg font-extrabold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{step.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-heading" className="section-title">Why refer ZuriHub</h2>
            <p className="section-subtitle mx-auto">
              ZuriHub is the customer experience platform modern teams switch to. Here is why it is easy to recommend.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whyRefer.map((w) => {
              const WhyIcon = w.icon === 'DollarSign' ? DollarSign : w.icon === 'Globe' ? Globe : w.icon === 'Repeat' ? Repeat : Shield;
              return (
                <div
                  key={w.title}
                  className="rounded-3xl border border-slate-100 bg-slate-50 p-8 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                    <WhyIcon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                  <p className="mt-3 text-slate-600">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="who-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="who-heading" className="section-title">Who can refer</h2>
            <p className="section-subtitle mx-auto">
              The program is open to anyone who believes ZuriHub can help a team grow.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whoCanRefer.map((w) => (
              <div
                key={w.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
              >
                <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                <p className="mt-3 text-slate-600">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="link-heading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 p-8 text-white shadow-2xl sm:p-12">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                <Gift className="h-5 w-5" />
              </div>
              <h2 id="link-heading" className="text-2xl font-bold">Your referral link</h2>
            </div>
            <p className="mt-4 text-brand-100">
              Create a workspace to unlock your personal code. Share this sample link to get started:
            </p>
            <div className="mt-6 flex items-center gap-2 rounded-xl bg-white/10 p-2 backdrop-blur">
              <div className="flex-1 truncate px-4 py-2 font-mono text-sm">{referralLink}</div>
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 transition"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm text-brand-100">Share via:</span>
              {shareLinks.map((s) => {
                const ShareIcon = s.icon === 'MessageSquare' ? MessageSquare : s.icon === 'Twitter' ? Twitter : s.icon === 'Linkedin' ? Linkedin : Mail;
                return (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/20 transition hover:bg-white/30"
                    aria-label={`Share on ${s.label}`}
                  >
                    <ShareIcon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="rules-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="rules-heading" className="section-title text-center">Referral rules</h2>
          <div className="mt-10 space-y-4">
            {rules.map((rule, i) => (
              <div
                key={i}
                className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <Check className="h-4 w-4" />
                </div>
                <p className="text-lg font-semibold text-slate-900">{rule}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <h3 className="font-bold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start earning today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Sign up for ZuriHub, get your personal referral link, and start sharing.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="gradient-cta mt-8 inline-flex items-center gap-2"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
